import React, { useState } from "react";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import { ReactComponent as DownloadIcon } from "../../../../assets/images/download-line.svg";
import { ReactComponent as FadeInIcon } from "../../../../assets/images/arrow-right-up-fill.svg";
import { ReactComponent as FadeOutIcon } from "../../../../assets/images/arrow-right-down-fill.svg";
import { ReactComponent as DragIcon } from "../../../../assets/images/expand-horizontal-line.svg";
import { ReactComponent as SplitIcon } from "../../../../assets/images/scissors-fill.svg";
import { useTranslation } from "react-i18next";
import { useAudioEditor } from "../../../../context/AudioEditorContext";
import { DEFAULT_ZOOM_INDEX, ZOOM_LEVELS } from "../constants";
import AudioEditorAddToProjectModal from "./AudioEditorAddToProjectModal";
import AudioEditorExportModal from "./AudioEditorExportModal";
import IconButton from "../../IconButton";
import { currentExportFilename } from "../AudioEditorTrackMixer/AudioEditorTrackMixer";
import styles from "./AudioEditorToolbar.module.css";

/** Solid glyphs (Claude ic-play / ic-pause / ic-square) — no outer ring so optical size matches other tools */
const PlayIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
);

const PauseIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <rect x="6" y="4" width="4" height="16" rx="1" />
        <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
);

const StopIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <rect x="5" y="5" width="14" height="14" rx="2" />
    </svg>
);
const CircleButton: React.FC<{
    onClick: () => void;
    children: React.ReactNode;
    isPlus?: boolean;
    disabled?: boolean;
    title?: string;
    disabledTitle?: string;
}> = ({ onClick, children, isPlus, disabled = false, title, disabledTitle }) => (
    <Tooltip title={disabled && disabledTitle ? disabledTitle : title || ""} arrow placement="top">
        <span>
            <button
                onClick={onClick}
                disabled={disabled}
                className={`${styles.circleButton} ${isPlus ? styles.circleButtonPlus : styles.circleButtonMinus} ${disabled ? styles.circleButtonDisabled : ""}`}
            >
                <span className={styles.circleGlyph} aria-hidden>
                    {children}
                </span>
            </button>
        </span>
    </Tooltip>
);

interface AddToProjectButtonProps {
    onClick: () => void;
    label?: string;
    disabled?: boolean;
    disabledTitle?: string;
}

const AddToProjectButton: React.FC<AddToProjectButtonProps> = ({ onClick, label, disabled = false, disabledTitle }) => (
    <div className={styles.addToProjectContainer}>
        <Tooltip title={disabled && disabledTitle ? disabledTitle : label || ""} arrow placement="top">
            <span>
                <button
                    onClick={onClick}
                    disabled={disabled}
                    className={`${styles.circleButton} ${styles.circleButtonPlus} ${disabled ? styles.circleButtonDisabled : ""}`}
                    type="button"
                >
                    <span className={styles.circleGlyph} aria-hidden>
                        +
                    </span>
                </button>
            </span>
        </Tooltip>
        {label && <span className={styles.addToProjectLabel}>{label}</span>}
    </div>
);

const AudioEditorToolbar: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
    // Local state to remember the last used filename
    const [lastFilename, setLastFilename] = useState<string>("export");

    const {
        newTrack,
        selectedAudio,
        tracks,
        playAudio,
        pauseAudio,
        stopAudio,
        exportAudio,
        exportAudioToProject,
        zoomIn,
        zoomOut,
        setZoomLevel,
        mode,
        setMode,
        isProcessing,
        isPlaying,
        setIsPlaying,
    } = useAudioEditor();

    const { t: rawT } = useTranslation();
    const t = rawT as any;

    // Define zoom level constants
    const ZOOM_MIN = 0;
    const ZOOM_MAX = ZOOM_LEVELS.length - 1;
    const ZOOM_STEP = 1;

    // Initialize zoom state with the middle level
    const [localZoomLevel, setLocalZoomLevel] = useState<number>(DEFAULT_ZOOM_INDEX);

    const handleZoomChange = (event: Event, value: number | number[]) => {
        if (typeof value !== "number") return;

        // Set directly to the new value
        if (setZoomLevel) {
            setZoomLevel(value);
        } else {
            // Fallback to old behavior if context doesn't provide setZoomLevel
            const diff = value - localZoomLevel;

            // Only call zoom functions for the exact number of steps changed
            const stepsToZoom = Math.abs(Math.round(diff));

            for (let i = 0; i < stepsToZoom; i++) {
                if (diff > 0) zoomIn?.();
                else if (diff < 0) zoomOut?.();
            }
        }

        setLocalZoomLevel(value);
    };

    const handleZoomInClick = () => {
        if (localZoomLevel < ZOOM_MAX) {
            zoomIn?.();
            setLocalZoomLevel((prev) => prev + 1);
        }
    };

    const handleZoomOutClick = () => {
        if (localZoomLevel > ZOOM_MIN) {
            zoomOut?.();
            setLocalZoomLevel((prev) => prev - 1);
        }
    };

    const handleAudioToTrack = () => {
        if (selectedAudio) {
            newTrack?.(selectedAudio);
        }
    };

    const handlePlayPause = () => {
        if (isPlaying) {
            pauseAudio?.();
        } else {
            playAudio?.();
        }
        setIsPlaying(!isPlaying);
    };

    const handleStop = () => {
        setIsPlaying(false);
        stopAudio?.();
    };

    const handleExport = () => {
        if (!isProcessing) {
            setIsExportModalOpen(true);
        }
    };

    const handleExportWithFilename = (filename: string) => {
        // Update our direct reference so it's immediately available
        currentExportFilename.value = filename;

        // Remember the filename for next time
        setLastFilename(filename);

        // Trigger the export
        exportAudio?.();
    };

    const handleSplitMode = () => {
        if (mode === "split") {
            setMode(null);
        } else {
            setMode("split");
        }
    };

    const handleDragMode = () => {
        if (mode === "drag") {
            setMode(null);
        } else {
            setMode("drag");
        }
    };

    const handleFadeInMode = () => {
        if (mode === "fadeIn") {
            setMode(null);
        } else {
            setMode("fadeIn");
        }
    };

    const handleFadeOutMode = () => {
        if (mode === "fadeOut") {
            setMode(null);
        } else {
            setMode("fadeOut");
        }
    };

    const hasAnyTracks = tracks && tracks.length > 0;

    return (
        <div className={styles.toolbar}>
            <div className={styles.tools}>
                <CircleButton
                    onClick={handleAudioToTrack}
                    isPlus
                    disabled={!selectedAudio}
                    title={t("add")}
                    disabledTitle={`${t("add")} ${t("disabled")}, ${t("noAudioSelected")}`}
                >
                    +
                </CircleButton>

                <IconButton
                    onClick={handlePlayPause}
                    disabled={!hasAnyTracks || (!playAudio && !pauseAudio)}
                    IconComponent={isPlaying ? PauseIcon : PlayIcon}
                    title={isPlaying ? t("pause") : t("play")}
                    disabledTitle={`${isPlaying ? t("pause") : t("play")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    iconSize={20}
                    isSelected={isPlaying}
                />

                <IconButton
                    onClick={handleStop}
                    disabled={!hasAnyTracks}
                    IconComponent={StopIcon}
                    title={t("stop")}
                    disabledTitle={`${t("stop")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    iconSize={20}
                />

                <IconButton
                    onClick={handleSplitMode}
                    disabled={!hasAnyTracks}
                    IconComponent={SplitIcon}
                    title={t("split")}
                    disabledTitle={`${t("split")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    iconSize={20}
                    isSelected={mode === "split"}
                />

                <span className={styles.toolSep} aria-hidden />

                <div className={`${styles.zoom} ${styles.zoomContainer}`}>
                    <CircleButton
                        onClick={handleZoomOutClick}
                        isPlus={false}
                        disabled={!hasAnyTracks}
                        title={t("zoomOut")}
                        disabledTitle={`${t("zoomOut")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    >
                        -
                    </CircleButton>

                    <Slider
                        value={localZoomLevel}
                        onChange={handleZoomChange}
                        min={ZOOM_MIN}
                        max={ZOOM_MAX}
                        step={ZOOM_STEP}
                        disabled={!hasAnyTracks}
                        className={styles.zoomSlider}
                    />

                    <CircleButton
                        onClick={handleZoomInClick}
                        isPlus={true}
                        disabled={!hasAnyTracks}
                        title={t("zoomIn")}
                        disabledTitle={`${t("zoomIn")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    >
                        +
                    </CircleButton>
                </div>

                <span className={styles.toolSep} aria-hidden />

                <IconButton
                    onClick={handleFadeOutMode}
                    disabled={!hasAnyTracks}
                    IconComponent={FadeOutIcon}
                    title={t("fadeOut")}
                    disabledTitle={`${t("fadeOut")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    iconSize={20}
                    isSelected={mode === "fadeOut"}
                />

                <IconButton
                    onClick={handleFadeInMode}
                    disabled={!hasAnyTracks}
                    IconComponent={FadeInIcon}
                    title={t("fadeIn")}
                    disabledTitle={`${t("fadeIn")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    iconSize={20}
                    isSelected={mode === "fadeIn"}
                />

                <IconButton
                    onClick={handleDragMode}
                    disabled={!hasAnyTracks}
                    IconComponent={DragIcon}
                    title={t("drag")}
                    disabledTitle={`${t("drag")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    iconSize={20}
                    isSelected={mode === "drag"}
                />

                <IconButton
                    onClick={handleExport}
                    disabled={!hasAnyTracks || !exportAudio || isProcessing}
                    IconComponent={DownloadIcon}
                    title={t("export")}
                    disabledTitle={`${t("export")} ${t("disabled")}, ${t("noTrackCreated")}`}
                    iconSize={20}
                />

                <AddToProjectButton
                    onClick={() => setIsModalOpen(true)}
                    label={t("addToProject")}
                    disabled={!hasAnyTracks || !exportAudioToProject || isProcessing}
                    disabledTitle={`${t("addToProject")} ${t("disabled")}, ${t("noTrackCreated")}`}
                />

                <AudioEditorAddToProjectModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onClick={() => !isProcessing && exportAudioToProject?.()}
                />

                <AudioEditorExportModal
                    isOpen={isExportModalOpen}
                    onClose={() => setIsExportModalOpen(false)}
                    onExport={handleExportWithFilename}
                    defaultFilename={lastFilename}
                />
            </div>
        </div>
    );
};

export default AudioEditorToolbar;
